import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { ArrowRight, FolderKanban, Plus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateUserProject, useOrgs, useUserProjects } from "@/queries/orgs";
import { useUserBilling } from "@/queries/billing";
import { useUser } from "@/queries/user";
import { getErrorMessage } from "@/utils/errors";

const FREE_PROJECT_LIMIT = 4;

export function DashboardPage() {
  const user = useUser();
  const userId = user.data?.user_id ?? "";
  const orgs = useOrgs();
  const myProjects = useUserProjects(userId);
  const billing = useUserBilling(userId);
  const createUserProject = useCreateUserProject(userId);
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");

  useEffect(() => {
    if (
      !orgs.isFetching &&
      !myProjects.isFetching &&
      orgs.data &&
      orgs.data.length === 0 &&
      myProjects.data &&
      myProjects.data.length === 0
    ) {
      navigate({ to: "/onboarding", replace: true });
    }
    // navigate is stable across renders; intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgs.data, orgs.isFetching, myProjects.data, myProjects.isFetching]);

  const plan = billing.data?.plan ?? "free";
  const ownedCount = myProjects.data?.length ?? 0;
  const atFreeLimit = plan === "free" && ownedCount >= FREE_PROJECT_LIMIT;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;
    try {
      await createUserProject.mutateAsync({ name: name.trim(), slug: slug.trim() });
      setName("");
      setSlug("");
      setShowCreate(false);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  return (
    <div className="space-y-6">
      <Helmet><title>TodoLLM - Dashboard</title></Helmet>
      <header className="space-y-2">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary">
          Dashboard
        </p>
        <h2 className="text-3xl font-semibold tracking-tight">Your workspace</h2>
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
          Personal projects and organizations you belong to.
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div>
            <CardTitle>Personal projects</CardTitle>
            <CardDescription>
              Owned by you. Free includes up to {FREE_PROJECT_LIMIT} projects;
              Pro unlocks unlimited projects.
            </CardDescription>
          </div>
          {atFreeLimit ? (
            <Button asChild size="sm" variant="outline">
              <Link to="/billing">
                <Plus className="size-4" />
                Upgrade to add more
              </Link>
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
              <Plus className="size-4" />
              {showCreate ? "Cancel" : "New project"}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {showCreate && (
            <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
              <div className="space-y-1">
                <Label htmlFor="np-name">Name</Label>
                <Input id="np-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="np-slug">Slug</Label>
                <Input
                  id="np-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))}
                  pattern="[a-z0-9\-]+"
                  required
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={createUserProject.isPending}>
                  {createUserProject.isPending ? "Creating…" : "Create"}
                </Button>
              </div>
            </form>
          )}
          {myProjects.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : myProjects.data && myProjects.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {myProjects.data.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: p.id }}
                      className="font-medium underline-offset-4 hover:underline"
                    >
                      {p.name}
                    </Link>
                    <p className="text-xs text-muted-foreground">/{p.slug}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{plan}</Badge>
                    <Button asChild variant="ghost" size="icon" aria-label="Open">
                      <Link to="/projects/$projectId" params={{ projectId: p.id }}>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No personal projects yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div>
            <CardTitle>Organizations</CardTitle>
            <CardDescription>
              Team plan only. Each organization holds projects, members, and the
              agents that work them.
            </CardDescription>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/billing">
              <Plus className="size-4" />
              Create organization
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {orgs.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : orgs.data && orgs.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {orgs.data.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <Link
                      to="/orgs/$orgId"
                      params={{ orgId: o.id }}
                      className="font-medium underline-offset-4 hover:underline"
                    >
                      {o.name}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      <FolderKanban className="mr-1 inline size-3" />
                      {o.project_count}{" "}
                      {o.project_count === 1 ? "project" : "projects"} · joined{" "}
                      {new Date(o.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{o.role}</Badge>
                    <Button asChild variant="ghost" size="icon" aria-label="Open">
                      <Link to="/orgs/$orgId" params={{ orgId: o.id }}>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No organizations yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
